import { useCallback, useEffect, useState } from "react";

import { Tabs as AntTabs } from "antd";
import CRM from "../CRM/CRM";
import CheckMatch from "components/CheckMatch/CheckMatch";
import LocationConfig from "components/LocationConfig/LocationConfig";
import TableStatus from "components/TableStatus/TableStatus";
import type { TabsProps } from "antd";
import ToastChits from "components/ToastChits/ToastChits";
import { useCRM } from "context/CRMContext";
import { useLocation } from "context/LocationContext";
import { useSearchParams } from "react-router-dom";
import { useTableStatus } from "context/TableStatusContext";

const ACTIVE_TAB_PARAM: string = "activeTab";

function Tabs() {
  const [activeTab, setActiveTab] = useState<string>();
  const [searchParams, setSearchParams] = useSearchParams();

  const { isTableStatusEnabled } = useTableStatus();

  const { currentLocation, locationDetails } = useLocation();

  const isCheckMatchEnabled = locationDetails?.checkMatchEnabled;

  const { isCRMEnabled } = useCRM();

  const posType = locationDetails?.posType;

  const isUpserveLocation = currentLocation?.posType === "upserve";
  const isLightspeedLocation = currentLocation?.posType === "lightspeed";
  const isNonPosLocation = currentLocation?.posType === "non-pos";

  const isLocationConfigDisabled = isNonPosLocation || isUpserveLocation || isLightspeedLocation;

  useEffect(() => {
    const param = searchParams.get(ACTIVE_TAB_PARAM);

    if (!param) {
      const tab = (isTableStatusEnabled && !isNonPosLocation) ? "tableStatus" :
        (isCheckMatchEnabled && !isNonPosLocation) ? "checkMatch" :
          (isCRMEnabled || isNonPosLocation) ? "crm" : "locationConfig";

      setSearchParams(`${ACTIVE_TAB_PARAM}=${tab}`);
    } else {
      setActiveTab(param);
    }
  }, [isCheckMatchEnabled, isCRMEnabled, isNonPosLocation, isTableStatusEnabled, searchParams, setSearchParams]);

  const handleActiveTab = useCallback(
    (tab: string) => {
      setSearchParams(`${ACTIVE_TAB_PARAM}=${tab}`);
      setActiveTab(tab);
    },
    [setSearchParams]
  );

  const items: TabsProps["items"] = [
    {
      key: "tableStatus",
      label: "Table Status",
      children: <TableStatus />,
      disabled: isNonPosLocation,
    },
    {
      key: "checkMatch",
      label: "Check Match",
      children: <CheckMatch />,
      disabled: isNonPosLocation,
    },
    {
      key: "locationConfig",
      label: "Location Config",
      children: <LocationConfig />,
      disabled: isLocationConfigDisabled,
    },
    {
      key: "crm",
      label: "CRM",
      children: <CRM />,
    },
    ...(posType === "toast" ? [{
      key: "toast-chits",
      label: "Toast Chits",
      children: <ToastChits />,
    }] : []),
  ];

  return (
    <AntTabs activeKey={activeTab} items={items} onTabClick={handleActiveTab} />
  );
}

export default Tabs;
